import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import {
  Banner,
  Checkbox,
  Input,
  Section,
  Select,
  SelectOption,
} from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../strings";
import {
  filmsGroundMutationErrorMessage,
  filmsGroundQueryKeys,
  NewRunSource,
  useStartNewRun,
} from "../filmsGround/service";
import {
  parseIconikAssetUrl,
  parseIconikCollectionUrl,
  parseIconikSavedSearchUrl,
} from "../filmsGround/iconikUrls";
import { useGamebookConfig, useGamebookWeeks } from "../gamebook/service";
import { seasonOptions } from "../gamebook/weeks";
import { gameWeekOptions } from "./gameWeekOptions";
import { controlRoomQueryKeys } from "./service";
import {
  CATEGORY_FIELD_NAME,
  DATE_FIELD_NAME,
  IngestCategory,
  IngestField,
  IngestFieldValue,
  IngestValues,
  INGEST_FUNCTION_FIELDS,
  INGEST_USER_INPUT_FIELDS,
  buildIngestPayload,
  categorySpecificFields,
  clearedForCategory,
  isIngestComplete,
  isIngestFieldFilled,
} from "./ingestFields";
import { requiredFieldInvalid, sourceFieldInvalid } from "./newRunValidation";

/**
 * "Start a new run" (Monday 12921140208, Seth: "kick off a Films Ground run from inside Spotter,
 * instead of having to drive it from outside") — the entry point Control Room was missing: today
 * it only ever OBSERVES jobs someone else already started. Shell is `@cinesys/ui`'s `Section` in
 * `modal` mode (Point 9 — do not re-implement `Section`): background click-outside, Escape to
 * close, Enter to submit, and the Cancel/Start footer pair are all `Section`'s own built-in
 * behavior, not hand-rolled here.
 *
 * SOURCE PICKER: no browse/search UI exists anywhere in this app to click through iconik's own
 * collection/saved-search/asset tree — reuses the SAME "paste the iconik link, parse the id out
 * of it" pattern Phase A's own start-clip field already established
 * (`AnchorReviewPanel.tsx`'s `startUrl`/`parseIconikAssetUrl`, now shared via `iconikUrls.ts`)
 * rather than inventing a picker UI or requiring a raw id. No display-name resolution before
 * submit either, for the same reason Phase A's own field doesn't show one: n8n resolves the real
 * name itself once it creates the job, exactly as it already does for a run started outside the
 * portal today (see `filmsGroundGenerate.ts`'s `FilmsGroundIntakeSource` doc comment).
 *
 * No existing job-selector component was reused here (and the one that briefly existed,
 * `AnchorJobPicker.tsx`'s tab strip, is deleted now anyway — design A4, Monday 12936679727): it
 * was a SELECTOR between jobs that already exist (`aria-pressed`/active-tab semantics tied to a
 * job list), not a creation form. Reusing it for "pick what a NEW run should be" would have bent
 * its whole contract around a case it wasn't built for — this modal's own picker below is a
 * plain form control instead.
 *
 * GAME PICKER: lists the whole season (`useGamebookWeeks` already returns every week, not just
 * generated ones — see `gameWeekOptions.ts`), per Seth's ask (Monday 12941628059): the first run
 * of the day was otherwise unable to select ANY game, since the old filtered-to-`generated` list
 * was empty until something had been generated that day. A run still has to be FOR an
 * already-ingested game — the backend resolves `gameId` from `(season, week)` itself, the exact
 * same way `GET /gamebook/matchup` does, and 404s for a week with no completed game — so an
 * ungenerated week is listed but `isDisabled`, its `extra` text stating why, rather than letting
 * the customer pick it and only finding out on submit.
 */

/** `Section` in `modal` mode, re-skinned to Spotter's own palette (customer-frontend CLAUDE.md
 *  convention) — same `styled(Section)` extension pattern `GamebookScreen.tsx`'s `TableCard`
 *  already establishes. `Section` renders its own background/border straight off `--dark100`/
 *  `--dark300`, which Spotter's theme already derives `--spotterSurface`/`--spotterLine` FROM
 *  (`SpotterThemeProvider.tsx`), so only the tokens `Select`/`Input` need beyond that are aliased
 *  here. */
const ModalSection = styled(Section)`
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
`;

/** Extra horizontal inset on top of `Section`'s own `pad` (20px/10px) — this form's two-Select
 *  rows and long option labels read as cramped at `Section`'s bare default. */
const ModalBody = styled.div`
  padding: 0 10px;
`;

const Intro = styled.p`
  margin: 0 0 20px;
  font-size: 13.5px;
  color: var(--spotterInkSoft);
  line-height: 1.5;
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-weight: 700;
  margin-bottom: 6px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;

  > div {
    flex: 1;
    min-width: 0;
  }
`;

const Hint = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: var(--spotterInkFaint);
`;

const InvalidHint = styled(Hint)`
  color: var(--spotterDanger);
`;

/** `portalTarget` anchor for every `Select` below (see `GamebookScreen.tsx`/`FieldsScreen.tsx`'s
 *  own `SelectPortalHost`) — without it, the modal body's own scroll clips a menu that grows
 *  taller than it. `display: contents` keeps it out of layout but still inside `ModalSection`, so
 *  the `--dark400`-etc. re-aliases above still reach the portalled menu. */
const SelectPortalHost = styled.div`
  display: contents;
`;

const SOURCE_KIND_OPTIONS: SelectOption[] = [
  { value: "collection", label: "Collection" },
  { value: "saved_search", label: "Saved search" },
  { value: "assets", label: "Assets" },
];

interface NewRunModalProps {
  onClose: () => void;
  /** Fired once the trigger POST has actually succeeded — lets `ControlRoomScreen` show its own
   *  transient "run started" banner without this modal owning that state past its own close. */
  onStarted: () => void;
}

const NewRunModal: React.FC<NewRunModalProps> = ({ onClose, onStarted }) => {
  const t = SPOTTER_STRINGS.controlRoom.newRun;
  const queryClient = useQueryClient();

  // Point 11 — a missed required field only turns red once the customer has actually tried to
  // submit once, not on first paint (`Section`'s own Enter-to-submit reaches `handleSubmit` too,
  // so this covers both the footer button and Enter).
  const [attempted, setAttempted] = useState(false);

  const [selectPortal, setSelectPortal] = useState<HTMLDivElement | null>(null);

  const [sourceKind, setSourceKind] =
    useState<NewRunSource["kind"]>("collection");
  const [collectionUrl, setCollectionUrl] = useState("");
  const [savedSearchUrl, setSavedSearchUrl] = useState("");
  const [assetUrlsText, setAssetUrlsText] = useState("");

  const configQuery = useGamebookConfig();
  const currentSeason = configQuery.data?.currentSeason;
  const [season, setSeason] = useState<number | undefined>(undefined);
  const [week, setWeek] = useState<string | undefined>(undefined);
  // Default to the current season once config loads — mirrors GamebookScreen's own
  // season-defaulting, but this modal has no per-browser "last season" to remember (it's a
  // one-shot form, not a persistent tab).
  useEffect(() => {
    if (season === undefined && currentSeason !== undefined) {
      setSeason(currentSeason);
    }
  }, [season, currentSeason]);

  const weeksQuery = useGamebookWeeks(season);
  const weeks = weeksQuery.data ?? [];
  const hasGeneratedWeek = weeks.some((w) => w.generated);
  const selectedWeekStatus = weeks.find((w) => w.week === week);

  const parsedCollectionId = parseIconikCollectionUrl(collectionUrl);
  const parsedSavedSearchId = parseIconikSavedSearchUrl(savedSearchUrl);
  const assetLines = assetUrlsText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const parsedAssetIds = assetLines
    .map((l) => parseIconikAssetUrl(l))
    .filter((id): id is string => !!id);
  const assetLinesInvalid =
    assetLines.length > 0 && parsedAssetIds.length !== assetLines.length;

  let source: NewRunSource | null = null;
  if (sourceKind === "collection" && parsedCollectionId) {
    source = { kind: "collection", collectionId: parsedCollectionId };
  } else if (sourceKind === "saved_search" && parsedSavedSearchId) {
    source = { kind: "saved_search", savedSearchId: parsedSavedSearchId };
  } else if (
    sourceKind === "assets" &&
    parsedAssetIds.length > 0 &&
    !assetLinesInvalid
  ) {
    source = { kind: "assets", assetIds: parsedAssetIds };
  }

  const [category, setCategory] = useState<IngestCategory | undefined>(
    undefined
  );
  const [ingestValues, setIngestValues] = useState<IngestValues>({});

  const setIngestValue = (
    name: string,
    value: IngestFieldValue | undefined
  ) => {
    setIngestValues((prev) => {
      if (value === undefined) {
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: value };
    });
  };

  // Switching Category clears every user-input value the new category no longer requires
  // (Seth: stale values "don't confuse the router") — see `clearedForCategory`'s doc comment.
  const handleCategoryChange = (next: IngestCategory) => {
    setCategory(next);
    setIngestValues((prev) => clearedForCategory(next, prev));
  };

  const dateField = INGEST_USER_INPUT_FIELDS.find(
    (f) => f.name === DATE_FIELD_NAME
  ) as IngestField;
  const categoryField = INGEST_USER_INPUT_FIELDS.find(
    (f) => f.name === CATEGORY_FIELD_NAME
  ) as IngestField;
  const extraIngestFields = categorySpecificFields(category);

  /** `invalid` reflects Point 11 (`attempted` gate) for every type except `boolean` — a function
   *  field (the only place `boolean` is ever passed) is never required, so there's nothing to
   *  flag on it. */
  const renderIngestField = (
    field: IngestField,
    invalid = false
  ): JSX.Element => {
    const value = ingestValues[field.name];
    switch (field.type) {
      case "date":
        return (
          <Input
            type="date"
            value={(value as string) ?? ""}
            onChange={(v) => setIngestValue(field.name, v || undefined)}
            noPadding
            invalid={invalid}
          />
        );
      case "string":
        return (
          <Input
            value={(value as string) ?? ""}
            onChange={(v) => setIngestValue(field.name, v || undefined)}
            placeholder={field.description}
            noPadding
            invalid={invalid}
          />
        );
      case "boolean":
        return (
          <Checkbox
            label={field.label}
            value={!!value}
            onChange={(v) => setIngestValue(field.name, v)}
          />
        );
      case "tag_cloud":
        // Free-entry tags (`options: []` — no fixed list, e.g. ClubUniform_AutoIngest):
        // `value` has to be full `{value, label}` pairs, not bare strings — Select falls back to
        // a literal "Unknown" label for any string it can't resolve against `options`, which is
        // every tag here since there IS no options list to resolve against.
        return (
          <Select
            isMulti
            creatable
            options={field.options}
            value={((value as string[]) ?? []).map((v) => ({
              value: v,
              label: v,
            }))}
            onChange={(opts) =>
              setIngestValue(
                field.name,
                (opts as SelectOption[]).map((o) => o.value as string)
              )
            }
            placeholder={t.tagCloudPlaceholder}
            noPadding
            portalTarget={selectPortal}
            invalid={invalid}
          />
        );
      case "drop_down":
      default:
        return (
          <Select
            options={field.options}
            value={(value as string) ?? undefined}
            onChange={(opt) =>
              setIngestValue(field.name, (opt as SelectOption)?.value as string)
            }
            hideClear
            noPadding
            portalTarget={selectPortal}
            invalid={invalid}
          />
        );
    }
  };

  const mutation = useStartNewRun();

  // `season`/`selectedWeekStatus.generated`, not just `!!week` — the week Select lists the whole
  // season now (`gameWeekOptions.ts`), and an ungenerated week can't back a run (no `gameId` to
  // resolve server-side); its option is `isDisabled` so the control shouldn't let one become
  // `week` in the first place, but this is the one guard standing between that and silently
  // submitting a run that can only 404.
  const formIncomplete =
    !source ||
    !season ||
    !selectedWeekStatus?.generated ||
    !isIngestComplete(category, ingestValues);

  const handleSubmit = () => {
    // Point 11 — flip the invalid states (and the summary banner below) first, whether or not the
    // form actually passes. Re-checked inline (not just `!formIncomplete`) so TS narrows `source`/
    // `selectedWeekStatus` for the `mutate()` call below — a separate `boolean` can't carry that.
    setAttempted(true);
    if (
      !source ||
      !season ||
      !selectedWeekStatus?.generated ||
      !isIngestComplete(category, ingestValues)
    ) {
      return;
    }
    mutation.mutate(
      {
        source,
        season,
        week: selectedWeekStatus.week,
        ingest: buildIngestPayload(ingestValues),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(filmsGroundQueryKeys.jobs);
          queryClient.invalidateQueries(controlRoomQueryKeys.jobs);
          onStarted();
          onClose();
        },
      }
    );
  };

  return createPortal(
    <ModalSection
      modal
      title={t.title}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={mutation.isLoading ? t.starting : t.start}
      submitLoading={mutation.isLoading}
      // Left enabled even when the form is incomplete — see Point 11: a disabled button gives no
      // feedback at all when a required field is missing, so it has to stay clickable for
      // `handleSubmit`'s `setAttempted(true)` to ever run and reveal the invalid states below.
      // Only genuinely block re-submitting while a request is already in flight.
      submitDisabled={mutation.isLoading}
      // No bespoke `width` — this is the only Section-modal consumer in the app that ever
      // overrode it; every other one just takes Section's own default (700px, still capped at
      // `max-width: 90vw` so it can't overflow a narrow viewport).
      pad
    >
      <ModalBody>
        <Intro>{t.intro}</Intro>

        <FieldGroup>
          <FieldLabel>{t.sourceKindLabel}</FieldLabel>
          <Select
            options={SOURCE_KIND_OPTIONS}
            value={sourceKind}
            onChange={(opt) => {
              const v = (opt as SelectOption)?.value;
              if (v) {
                setSourceKind(v as NewRunSource["kind"]);
              }
            }}
            hideClear
            noPadding
            portalTarget={selectPortal}
          />
        </FieldGroup>

        {sourceKind === "collection" && (
          <FieldGroup>
            <FieldLabel>{t.collectionLabel}</FieldLabel>
            <Input
              value={collectionUrl}
              onChange={setCollectionUrl}
              placeholder={t.collectionPlaceholder}
              clearable
              noPadding
              invalid={sourceFieldInvalid(
                attempted,
                !!source,
                collectionUrl.trim().length > 0 && !parsedCollectionId
              )}
            />
            {collectionUrl.trim().length > 0 && !parsedCollectionId && (
              <InvalidHint>{t.linkInvalid}</InvalidHint>
            )}
          </FieldGroup>
        )}

        {sourceKind === "saved_search" && (
          <FieldGroup>
            <FieldLabel>{t.savedSearchLabel}</FieldLabel>
            <Input
              value={savedSearchUrl}
              onChange={setSavedSearchUrl}
              placeholder={t.savedSearchPlaceholder}
              clearable
              noPadding
              invalid={sourceFieldInvalid(
                attempted,
                !!source,
                savedSearchUrl.trim().length > 0 && !parsedSavedSearchId
              )}
            />
            {savedSearchUrl.trim().length > 0 && !parsedSavedSearchId && (
              <InvalidHint>{t.linkInvalid}</InvalidHint>
            )}
          </FieldGroup>
        )}

        {sourceKind === "assets" && (
          <FieldGroup>
            <FieldLabel>{t.assetsLabel}</FieldLabel>
            <Input
              textarea
              value={assetUrlsText}
              onChange={setAssetUrlsText}
              placeholder={t.assetsPlaceholder}
              noPadding
              invalid={sourceFieldInvalid(
                attempted,
                !!source,
                assetLinesInvalid
              )}
            />
            {assetLinesInvalid ? (
              <InvalidHint>{t.linkInvalid}</InvalidHint>
            ) : (
              parsedAssetIds.length > 0 && (
                <Hint>{t.assetsCount(parsedAssetIds.length)}</Hint>
              )
            )}
          </FieldGroup>
        )}

        <FieldGroup>
          <FieldLabel>{t.gameLabel}</FieldLabel>
          {!configQuery.data?.clubCode && !configQuery.isLoading ? (
            <Hint>{SPOTTER_STRINGS.gamebook.notConfigured}</Hint>
          ) : (
            <>
              <Row>
                <div>
                  <Select
                    options={seasonOptions(
                      currentSeason ?? new Date().getFullYear()
                    ).map((s) => ({ value: String(s), label: String(s) }))}
                    value={season !== undefined ? String(season) : undefined}
                    onChange={(opt) => {
                      const v = (opt as SelectOption)?.value;
                      if (v) {
                        setSeason(parseInt(v, 10));
                        setWeek(undefined);
                      }
                    }}
                    hideClear
                    noPadding
                    disabled={configQuery.isLoading}
                    portalTarget={selectPortal}
                    invalid={requiredFieldInvalid(attempted, !!season)}
                  />
                </div>
                <div>
                  <Select
                    options={gameWeekOptions(weeks)}
                    value={week}
                    onChange={(opt) => {
                      const v = (opt as SelectOption)?.value;
                      setWeek(v || undefined);
                    }}
                    isSearchable
                    hideClear
                    noPadding
                    disabled={weeksQuery.isLoading || weeks.length === 0}
                    placeholder={
                      !weeksQuery.isLoading && weeks.length === 0
                        ? t.noWeeksAvailable
                        : undefined
                    }
                    portalTarget={selectPortal}
                    invalid={requiredFieldInvalid(
                      attempted,
                      !!selectedWeekStatus?.generated
                    )}
                  />
                </div>
              </Row>
              {!weeksQuery.isLoading &&
                weeks.length > 0 &&
                !hasGeneratedWeek && <Hint>{t.noGeneratedWeeksHint}</Hint>}
            </>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>{categoryField.label}</FieldLabel>
          <Select
            options={categoryField.options}
            value={category}
            onChange={(opt) => {
              const v = (opt as SelectOption)?.value as
                | IngestCategory
                | undefined;
              if (v) {
                handleCategoryChange(v);
              }
            }}
            hideClear
            noPadding
            portalTarget={selectPortal}
            invalid={requiredFieldInvalid(attempted, !!category)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>{dateField.label}</FieldLabel>
          {renderIngestField(
            dateField,
            requiredFieldInvalid(
              attempted,
              isIngestFieldFilled(dateField, ingestValues[dateField.name])
            )
          )}
        </FieldGroup>

        {extraIngestFields.map((field) => (
          <FieldGroup key={field.name}>
            <FieldLabel>{field.label}</FieldLabel>
            {renderIngestField(
              field,
              requiredFieldInvalid(
                attempted,
                isIngestFieldFilled(field, ingestValues[field.name])
              )
            )}
          </FieldGroup>
        ))}

        {INGEST_FUNCTION_FIELDS.map((field) =>
          field.type === "boolean" ? (
            <FieldGroup key={field.name}>{renderIngestField(field)}</FieldGroup>
          ) : (
            <FieldGroup key={field.name}>
              <FieldLabel>{field.label}</FieldLabel>
              {renderIngestField(field)}
            </FieldGroup>
          )
        )}

        {/* A consolidated message next to Start, once an attempt has actually failed the form
         check — not a substitute for the per-field states above, just confirmation the click
         didn't silently do nothing. Superseded by a real mutation error once one exists. */}
        {formIncomplete && attempted ? (
          <Banner error={t.formIncomplete} />
        ) : (
          mutation.isError && (
            <Banner
              error={filmsGroundMutationErrorMessage(
                mutation.error,
                t.startError
              )}
            />
          )
        )}
      </ModalBody>
      <SelectPortalHost ref={setSelectPortal} />
    </ModalSection>,
    document.body
  );
};

export default NewRunModal;
